import { useEffect, useId, useRef, useState } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: {
        sitekey: string
        callback?: (token: string) => void
        'expired-callback'?: () => void
        'error-callback'?: () => void
        theme?: 'light' | 'dark' | 'auto'
      }) => string
      remove: (widgetId: string) => void
      reset: (widgetId?: string) => void
    }
  }
}

interface TurnstileWidgetProps {
  siteKey: string
  onTokenChange: (token: string | null) => void
}

export function TurnstileWidget({ siteKey, onTokenChange }: TurnstileWidgetProps) {
  const elementId = useId().replace(/:/g, '')
  const widgetIdRef = useRef<string | null>(null)
  const [isReady, setIsReady] = useState(Boolean(window.turnstile))

  useEffect(() => {
    if (window.turnstile) {
      setIsReady(true)
      return
    }

    const interval = window.setInterval(() => {
      if (window.turnstile) {
        setIsReady(true)
        window.clearInterval(interval)
      }
    }, 100)

    return () => window.clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!isReady || !window.turnstile || widgetIdRef.current) return

    widgetIdRef.current = window.turnstile.render(`#${elementId}`, {
      sitekey: siteKey,
      theme: 'auto',
      callback: (token) => onTokenChange(token),
      'expired-callback': () => onTokenChange(null),
      'error-callback': () => onTokenChange(null),
    })

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [elementId, isReady, onTokenChange, siteKey])

  return <div id={elementId} className="min-h-16" />
}
